/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { handleNetworkError } from '@/auth/lib/network-error';
import { fetchGraphQl } from '../fetch-graphql';
import { GET_CUSTOMER_TOKEN } from './graphql/getCustomerToken.graphql';
import { getCustomerData } from '../getCustomerData';
import { InLineAlertInterface } from '@/auth/types';
import { events } from '@adobe-commerce/event-bus';
import { COOKIE_NAMES, LOCALHOST } from '@/auth/configs/cookieConfigs';
import { getCookiesLifetime } from '@/auth/lib/cookieUtils';
import { publishEvents, EventsList } from '@/auth/lib/acdl';
import { setReCaptchaToken } from '@/auth/lib/setReCaptchaToken';
import { emitAuthGroupIdEvent } from '@/auth/lib/emitAuthGroupId';

interface getCustomerTokenProps {
  email: string;
  password: string;
  handleSetInLineAlertProps: (value?: InLineAlertInterface) => void;
  translations: Record<string, string>;
  onErrorCallback?: (value?: unknown) => void;
  apiErrorMessageOverride?: string;
}

export const getCustomerToken = async ({
  email,
  password,
  translations,
  onErrorCallback,
  handleSetInLineAlertProps,
  apiErrorMessageOverride,
}: getCustomerTokenProps): Promise<{
  errorMessage: string;
  displayErrorMessage: string;
  userName: string;
  userEmail: string;
}> => {
  await setReCaptchaToken();

  const response = await fetchGraphQl(GET_CUSTOMER_TOKEN, {
    method: 'POST',
    variables: { email, password },
  }).catch(handleNetworkError);

  if (!response?.data?.generateCustomerToken?.token) {
    // Fallback error message
    const defaultErrorMessage = translations.customerTokenErrorMessage;
    // Original error message from API or default (used for error detection logic)
    const errorMessage = response?.errors
      ? response.errors[0].message
      : defaultErrorMessage;
    // Display error message (override if provided, otherwise original)
    const displayErrorMessage = apiErrorMessageOverride ?? errorMessage;

    onErrorCallback?.(errorMessage);
    handleSetInLineAlertProps?.({ type: 'error', text: displayErrorMessage });

    return { errorMessage, displayErrorMessage, userName: '', userEmail: '' };
  }

  const userToken = response?.data?.generateCustomerToken?.token;

  const responseCustomer = await getCustomerData(userToken);
  const userName = responseCustomer?.firstName;
  const userEmail = responseCustomer?.email;

  if (!userName || !userEmail) {
    const errorMessage = translations.customerTokenErrorMessage;
    const displayErrorMessage = apiErrorMessageOverride ?? errorMessage;

    onErrorCallback?.(errorMessage);
    handleSetInLineAlertProps?.({ type: 'error', text: displayErrorMessage });

    return { errorMessage, displayErrorMessage, userName: '', userEmail: '' };
  }

  const cookiesLifetime = await getCookiesLifetime();

  // Do not set 'Secure' flag on auth cookies when running on localhost (fix for Safari browser)
  const cookieSecureFlag: string | null = LOCALHOST.includes(window.location.hostname) ? '' : 'Secure';

  document.cookie = `${COOKIE_NAMES.auth_dropin_firstname}=${userName}; path=/; ${cookiesLifetime}; ${cookieSecureFlag};`;
  document.cookie = `${COOKIE_NAMES.auth_dropin_user_token}=${userToken}; path=/; ${cookiesLifetime}; ${cookieSecureFlag};`;

  await emitAuthGroupIdEvent(userToken ? responseCustomer?.groupUid : undefined);
  events.emit('authenticated', !!userToken);

  publishEvents(EventsList?.SIGN_IN, { ...responseCustomer });

  return { errorMessage: '', displayErrorMessage: '', userName, userEmail };
};
